/**
 * The send / copy-link / revoke flow for one organization's invitations (spec §6.3), as a
 * framework-free controller so every rule is unit-tested under the `node` vitest environment:
 *   - a resend is confirmed because it rotates the link;
 *   - at most one action per invitation is in flight (send, revoke, copy, or the reconcile after
 *     a failed send), tracked per id, so a second click on the same row is refused while a
 *     different row can proceed;
 *   - the per-invitation send key survives a 5xx / network failure, the rate-limit answers (the
 *     server checks the cooldown before the idempotency claim) and 409 send_in_progress (the
 *     server released this key's claim), so a retry after a lost response presents the original
 *     key and gets 409 send_already_processed, never a second email;
 *   - send_already_processed is only a candidate success: it is announced as Sent only when the
 *     awaited reconcile brings a row whose SendCount rose (`sendCountIncreased`); otherwise the
 *     row shows `alreadySentMessage` and nothing is announced. Each invitation keeps a
 *     SendLockCause (`nextSendLockCause`), so while the server may still hold its send lock a
 *     send_in_progress is worded from that cause, not as a 2 s wait. Nothing is ever resent
 *     automatically;
 *   - a successful send hands its returned DTO to the caller (`onSent`) before the row is
 *     released, so the row shows the authoritative link and count without waiting for a reload;
 *   - every send failure, send_already_processed and every revoke AWAIT the caller's reload
 *     (`onReconcile`): the service persists a rotated token before emailing, so the link may
 *     already have changed; a failure with no status (a network error, or the 30 s client
 *     timeout) is ambiguous, because the server may have rotated the token and only the response
 *     was lost; a revoke leaves the row showing Pending with a dead link until the reload. The
 *     row stays pending until a reload the caller ACCEPTED lands (one it discarded as superseded
 *     is read again, at most RECONCILE_MAX_READS reads), and if the reload fails the row stays
 *     disabled with "Could not refresh this invitation." and a Retry (`retryReconcile`); the
 *     stale "Copied" cue is dropped;
 *   - Copy holds the row pending while the clipboard write is outstanding, and shows "Copied"
 *     only if the invitation's CURRENT acceptUrl is still the one it wrote; otherwise it shows the
 *     fallback, keyed by invitation id, and the views render the CURRENT acceptUrl there; when
 *     the invitation turned terminal meanwhile it shows neither, because no usable link exists;
 *   - a clipboard refusal falls back to a selectable URL;
 *   - after `dispose()` (an org switch or the page's unmount) nothing runs: a
 *     send, reconcile, clipboard write, cooldown tick or cue timeout that settles later touches
 *     no state and calls no dependency (no onSent, no reload, no currentAcceptUrl), and new
 *     actions are refused.
 * `useInvitationActions` is the thin React wrapper that binds the dependencies;
 * OrgDetailPage calls it once per organization and passes the result to the panel and the invite
 * dialog, so a tab switch or a dialog close ends nothing. Because both render over
 * one controller, the sent cue and the action error name the invitation they are about.
 */
import type { AdminInvitation } from '../services/adminApi';
import {
  alreadySentMessage,
  applyCooldownTick,
  keepSendRequestIdAfterFailure,
  mapInvitationActionFailure,
  mapSendFailure,
  nextSendLockCause,
  readApiFailure,
  sendCountIncreased,
  startSendCooldown,
  type SendBlock,
  type SendCooldownCause,
  type SendFailure,
  type SendLockCause,
} from './orgProvisioning';

/** 'reconcile': the caller's reload after a failed send is running, or failed (see reconcileFailed). */
export type PendingAction = 'send' | 'revoke' | 'copy' | 'reconcile';

export interface SentCue {
  /** The invitation the cue is about; the invite dialog shows only its own. */
  invitationId: string;
  email: string;
  at: Date;
}

export interface InvitationActionsState {
  /** Invitation ids with an action in flight; a second action for the same id is refused. */
  pending: Record<string, PendingAction>;
  /** Ids whose post-failure reload failed; the row stays pending at 'reconcile' and offers Retry. */
  reconcileFailed: Record<string, true>;
  copiedInvitationId: string | null;
  /** The invitation whose link the clipboard refused (or that rotated mid-copy); the views show its CURRENT acceptUrl. */
  copyFallbackInvitationId: string | null;
  sentCue: SentCue | null;
  actionError: string | null;
  /** The invitation `actionError` is about (the invite dialog shows only its own), or null. */
  actionErrorInvitationId: string | null;
  /**
   * Per invitation: a running cooldown after 429 send_too_soon, the limit after 409
   * send_limit_reached, or a 2 s wait after 409 send_in_progress.
   */
  sendBlocks: Record<string, SendBlock>;
}

export interface InvitationActionsDeps {
  sendInvitation: (invitationId: string, requestId: string) => Promise<AdminInvitation>;
  revokeInvitation: (invitationId: string) => Promise<void>;
  confirm: (message: string) => boolean;
  writeClipboard: (text: string) => Promise<void>;
  now: () => Date;
  newRequestId: () => string;
  /**
   * The invitation's CURRENT acceptUrl as the caller holds it, or null once it is no longer
   * pending and unexpired (`usableAcceptUrl`); read when a clipboard write settles.
   */
  currentAcceptUrl: (invitationId: string) => string | null;
  /**
   * The invitation's CURRENT row as the caller holds it, or undefined; read after the awaited
   * reconcile of a send_already_processed to confirm it by its SendCount.
   */
  currentInvitation: (invitationId: string) => AdminInvitation | undefined;
  /** Receives the authoritative DTO a successful send returned, before the row is released. */
  onSent: (invitation: AdminInvitation) => void;
  /**
   * Called after every send failure (with or without a status), after send_already_processed and
   * after every revoke: the caller reloads the invitation(s) and resolves true once the fresh row
   * is in its state, false when that reload's answer was discarded because a later read or an
   * applied send DTO superseded it (the controller then reads again), or rejects when it failed.
   * The controller awaits it; the row's actions stay disabled until it resolves true.
   */
  onReconcile: () => Promise<boolean>;
  onChange: (state: InvitationActionsState) => void;
}

export interface InvitationActions {
  getState: () => InvitationActionsState;
  /**
   * Resolves to the DTO a successful send returned, or null otherwise (refused, cancelled,
   * blocked, failed, or send_already_processed, whose authoritative row arrives through the
   * awaited reconcile).
   */
  send: (invitation: AdminInvitation) => Promise<AdminInvitation | null>;
  copyLink: (invitation: AdminInvitation) => Promise<void>;
  /** Resolves true when the invitation was revoked; either way only after the awaited reconcile. */
  revoke: (invitation: AdminInvitation) => Promise<boolean>;
  /** Re-runs a failed reconcile; resolves true once it succeeded and the row is released. */
  retryReconcile: (invitationId: string) => Promise<boolean>;
  /**
   * Ends the controller: cancels cooldown timers and cue timeouts, and makes every continuation
   * still in flight inert (it touches no state and calls no dependency when it settles); later
   * actions are refused. Idempotent.
   */
  dispose: () => void;
}

export const COPY_FAILED_MESSAGE = 'Could not copy; select the link below';
export const LINK_CHANGED_MESSAGE = 'The link changed while copying; select the new link below';
export const RECONCILE_FAILED_MESSAGE = 'Could not refresh this invitation.';
export const LINK_UNAVAILABLE_MESSAGE = 'This invitation is no longer pending, so its link no longer works.';
const COPIED_CUE_MS = 2000;
const SENT_CUE_MS = 6000;
const SEND_IN_PROGRESS_MS = 2000;
/** Reads per reconcile while each answer is discarded as superseded; then the Retry path. */
const RECONCILE_MAX_READS = 3;

export const INITIAL_INVITATION_ACTIONS_STATE: InvitationActionsState = {
  pending: {},
  reconcileFailed: {},
  copiedInvitationId: null,
  copyFallbackInvitationId: null,
  sentCue: null,
  actionError: null,
  actionErrorInvitationId: null,
  sendBlocks: {},
};

export function resendConfirmation(email: string): string {
  return `Resend to ${email}? The previously copied link stops working and a new one will be issued.`;
}

export function revokeConfirmation(email: string): string {
  return `Revoke the invitation for ${email}? Its link stops working immediately.`;
}

export function formatSentCue(cue: SentCue): string {
  return `Sent to ${cue.email} at ${cue.at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

export function createInvitationActions(deps: InvitationActionsDeps): InvitationActions {
  let state = INITIAL_INVITATION_ACTIONS_STATE;
  // Per invitation, the kept send key and the SendCount of the row it was minted from: the
  // baseline that proves a send for this key happened once a reconciled row's count rises above it.
  const sendKeys = new Map<string, { requestId: string; baseSendCount: number }>();
  // Per invitation, the decision a send failure still owes once its reconcile lands; set when that
  // reconcile failed and run when retryReconcile succeeds.
  const afterReconcile = new Map<string, () => void>();
  // Per invitation, why the server may still hold its send lock (absent: null).
  const sendLockCauses = new Map<string, SendLockCause>();
  const setSendLockCause = (id: string, cause: SendLockCause) => {
    if (cause === null) sendLockCauses.delete(id);
    else sendLockCauses.set(id, cause);
  };
  const cooldownCancels = new Map<string, () => void>();
  const timers = new Set<ReturnType<typeof setTimeout>>();
  // dispose() sets `disposed` and bumps `generation`. Each action captures the generation when it
  // starts, and every continuation (after an await, in a timer, in a cooldown tick) checks
  // alive(gen) before touching state or calling a dependency, so nothing started before an
  // org switch or the page's unmount can publish after it.
  let disposed = false;
  let generation = 0;
  const alive = (gen: number) => !disposed && gen === generation;

  const update = (patch: Partial<InvitationActionsState>) => {
    if (disposed) return;
    state = { ...state, ...patch };
    deps.onChange(state);
  };

  const later = (ms: number, run: () => void) => {
    const timer = setTimeout(() => {
      timers.delete(timer);
      if (!disposed) run();
    }, ms);
    timers.add(timer);
  };

  const setPending = (id: string, action: PendingAction | null) => {
    const pending = { ...state.pending };
    if (action) pending[id] = action;
    else delete pending[id];
    update({ pending });
  };

  const setReconcileFailed = (id: string, failed: boolean) => {
    if (failed === (id in state.reconcileFailed)) return;
    const reconcileFailed = { ...state.reconcileFailed };
    if (failed) reconcileFailed[id] = true;
    else delete reconcileFailed[id];
    update({ reconcileFailed });
  };

  const clearSendBlock = (id: string) => {
    if (!(id in state.sendBlocks)) return;
    const sendBlocks = { ...state.sendBlocks };
    delete sendBlocks[id];
    update({ sendBlocks });
  };

  const forgetCopied = (id: string) => {
    if (state.copiedInvitationId === id) update({ copiedInvitationId: null });
  };

  const forgetFallback = (id: string) => {
    if (state.copyFallbackInvitationId === id) update({ copyFallbackInvitationId: null });
  };

  const announceSent = (invitationId: string, email: string, at: Date = deps.now()) => {
    const cue = { invitationId, email, at };
    update({ sentCue: cue });
    later(SENT_CUE_MS, () => {
      if (state.sentCue === cue) update({ sentCue: null });
    });
  };

  // Holds the row at 'reconcile' until a reload the caller ACCEPTED lands, so Send, Resend, Copy
  // and Revoke cannot act on a link the server may already have rotated. A reload whose answer
  // the caller discarded as superseded proves nothing about this row, so it is read again. On
  // failure (or RECONCILE_MAX_READS superseded reads) the row stays pending, marked in
  // reconcileFailed, and the views offer Retry (retryReconcile). Once disposed it stops where it
  // is: no further read, no release, no Retry state.
  const reconcileLink = async (id: string, gen: number): Promise<boolean> => {
    if (!alive(gen)) return false;
    setPending(id, 'reconcile');
    setReconcileFailed(id, false);
    try {
      let reads = 1;
      while (!(await deps.onReconcile())) {
        if (!alive(gen)) return false;
        if (reads >= RECONCILE_MAX_READS) throw new Error('The reload kept being superseded');
        reads += 1;
      }
    } catch {
      if (!alive(gen)) return false;
      setReconcileFailed(id, true);
      return false;
    }
    if (!alive(gen)) return false;
    setPending(id, null);
    return true;
  };

  const startCooldown = (id: string, gen: number, seconds: number, cause: SendCooldownCause | undefined) => {
    cooldownCancels.get(id)?.();
    cooldownCancels.delete(id);
    let cancel: (() => void) | undefined;
    let finished = false;
    cancel = startSendCooldown(seconds, (secondsLeft) => {
      if (!alive(gen)) return;
      update({ sendBlocks: applyCooldownTick(state.sendBlocks, id, secondsLeft, cause) });
      if (secondsLeft <= 0) {
        finished = true;
        if (cancel && cooldownCancels.get(id) === cancel) cooldownCancels.delete(id);
      }
    });
    if (!finished) cooldownCancels.set(id, cancel);
  };

  // The decision an already_sent candidate waits for: confirmed by the reconciled row's SendCount
  // against the key's own baseline, it is announced as Sent; otherwise the row shows
  // alreadySentMessage. Only then does the lock cause move.
  const confirmAlreadySent = (inv: AdminInvitation, baseSendCount: number, lockCause: SendLockCause, failure: SendFailure) => {
    const confirmed = sendCountIncreased({ sendCount: baseSendCount }, deps.currentInvitation(inv.id));
    const message = alreadySentMessage(lockCause, confirmed);
    if (message === null) announceSent(inv.id, inv.email);
    else update({ actionError: message, actionErrorInvitationId: inv.id });
    setSendLockCause(inv.id, nextSendLockCause(lockCause, failure, { confirmed }));
  };

  // After any other failure: a reconciled count above the key's baseline proves that a send
  // happened (a lost but stamped response, or another staff member's send), so the key is dropped,
  // the cause cleared and Sent announced at the reconciled row's lastSentAt, the latest send's own
  // time; the next press is a real resend with a new key.
  const proveLostSend = (inv: AdminInvitation, baseSendCount: number) => {
    if (!sendKeys.has(inv.id)) return;
    const current = deps.currentInvitation(inv.id);
    if (!sendCountIncreased({ sendCount: baseSendCount }, current)) return;
    sendKeys.delete(inv.id);
    setSendLockCause(inv.id, null);
    if (state.actionErrorInvitationId === inv.id) update({ actionError: null, actionErrorInvitationId: null });
    announceSent(inv.id, inv.email, current?.lastSentAt ? new Date(current.lastSentAt) : undefined);
  };

  // Awaits the reconcile, then runs `decide`; when the reconcile failed, `decide` waits for a
  // successful retryReconcile instead, and nothing is decided meanwhile.
  const reconcileThen = async (id: string, gen: number, decide: () => void) => {
    afterReconcile.delete(id);
    const released = await reconcileLink(id, gen);
    if (!alive(gen)) return;
    if (released) decide();
    else if (id in state.reconcileFailed) afterReconcile.set(id, decide);
  };

  const send = async (inv: AdminInvitation): Promise<AdminInvitation | null> => {
    if (disposed || inv.id in state.pending || inv.id in state.sendBlocks) return null;
    if (inv.sendCount > 0 && !deps.confirm(resendConfirmation(inv.email))) return null;
    const gen = generation;
    const key = sendKeys.get(inv.id) ?? { requestId: deps.newRequestId(), baseSendCount: inv.sendCount };
    sendKeys.set(inv.id, key);
    setPending(inv.id, 'send');
    update({ actionError: null, actionErrorInvitationId: null });
    let sent: AdminInvitation;
    try {
      sent = await deps.sendInvitation(inv.id, key.requestId);
    } catch (err: unknown) {
      // Disposed meanwhile (an org switch or the page's unmount): no state, and no reload of an
      // org the page may have left.
      if (!alive(gen)) return null;
      const { status, code } = readApiFailure(err);
      if (!keepSendRequestIdAfterFailure(status, code)) sendKeys.delete(inv.id);
      const lockCause = sendLockCauses.get(inv.id) ?? null;
      const failure = mapSendFailure(err, { lockCause });
      if (failure.kind === 'already_sent') {
        // 409 send_already_processed: the server already processed this key (a replayed click
        // after a lost response), so the key is dropped. It is only a candidate success and
        // carries no DTO: await the reconcile, then confirm it by the reconciled row's SendCount
        // against the key's baseline before announcing anything. An unchanged count never means
        // "not sent".
        sendKeys.delete(inv.id);
        forgetCopied(inv.id);
        await reconcileThen(inv.id, gen, () => confirmAlreadySent(inv, key.baseSendCount, lockCause, failure));
        return null;
      }
      setSendLockCause(inv.id, nextSendLockCause(lockCause, failure));
      if (failure.kind === 'too_soon') {
        // Count the cooldown down and enable the control again at 0; nothing is resent by itself.
        startCooldown(inv.id, gen, failure.retryAfterSeconds, failure.cause);
      } else if (failure.kind === 'limit_reached') {
        update({
          sendBlocks: {
            ...state.sendBlocks,
            [inv.id]: { kind: 'limit', sendCount: failure.sendCount, limit: failure.limit },
          },
        });
      } else if (failure.kind === 'in_progress') {
        // 409 send_in_progress: another staff member's send for this invitation is running. Say
        // so and enable Send again after 2 s; nothing is resent by itself, and the key is kept.
        cooldownCancels.get(inv.id)?.();
        cooldownCancels.delete(inv.id);
        update({ sendBlocks: { ...state.sendBlocks, [inv.id]: { kind: 'in_progress' } } });
        later(SEND_IN_PROGRESS_MS, () => {
          if (state.sendBlocks[inv.id]?.kind === 'in_progress') clearSendBlock(inv.id);
        });
      } else {
        update({ actionError: failure.message, actionErrorInvitationId: inv.id });
      }
      // Every failure waits for the caller's reload. With a status the request reached the
      // server, which persists a rotated token before emailing; without one (a network error, or
      // the 30 s client timeout) it is ambiguous: the server may have rotated the token and only
      // the response was lost. Either way the link may have changed, so the row stays at
      // 'reconcile' (Retry if the reload fails), and the key kept above serves the retry unless
      // the reconciled count proves the send happened.
      forgetCopied(inv.id);
      // A 502 invitation_send_failed is a definite answer that this request sent nothing, so a
      // risen count is someone else's send and its "nothing went out" message stands.
      const definiteNonSend = code === 'invitation_send_failed';
      await reconcileThen(inv.id, gen, () => {
        if (!definiteNonSend) proveLostSend(inv, key.baseSendCount);
      });
      return null;
    }
    // Disposed meanwhile (an org switch or the page's unmount): the DTO is not handed on, so it cannot
    // overwrite a newer page update or land in another org's list.
    if (!alive(gen)) return null;
    sendKeys.delete(inv.id);
    setSendLockCause(inv.id, nextSendLockCause(sendLockCauses.get(inv.id) ?? null, null));
    forgetCopied(inv.id);
    // The returned DTO is authoritative: the caller applies it now, before and independent of
    // any list reload, and only then is the row released.
    deps.onSent(sent);
    announceSent(inv.id, inv.email);
    setPending(inv.id, null);
    return sent;
  };

  const copyLink = async (inv: AdminInvitation): Promise<void> => {
    const url = inv.acceptUrl;
    if (disposed || !url || inv.id in state.pending) return;
    const gen = generation;
    setPending(inv.id, 'copy');
    let written = true;
    try {
      await deps.writeClipboard(url);
    } catch {
      written = false;
    }
    if (!alive(gen)) return;
    setPending(inv.id, null);
    const current = deps.currentAcceptUrl(inv.id);
    const otherFallback = state.copyFallbackInvitationId === inv.id ? null : state.copyFallbackInvitationId;
    if (current === null) {
      // The invitation turned terminal while the write was outstanding (a reload or a send DTO
      // brought Accepted, AutoResolved, Revoked or Expired): no link works any more, so neither
      // "Copied" nor a fallback.
      update({ copiedInvitationId: null, copyFallbackInvitationId: otherFallback, actionError: LINK_UNAVAILABLE_MESSAGE, actionErrorInvitationId: inv.id });
      return;
    }
    if (!written) {
      update({ copiedInvitationId: null, copyFallbackInvitationId: inv.id, actionError: COPY_FAILED_MESSAGE, actionErrorInvitationId: inv.id });
      return;
    }
    if (current !== url) {
      // The link rotated while the clipboard write was outstanding (a reload brought another
      // staff member's resend): what was copied no longer works, so no "Copied"; the fallback
      // shows the CURRENT link instead.
      update({ copiedInvitationId: null, copyFallbackInvitationId: inv.id, actionError: LINK_CHANGED_MESSAGE, actionErrorInvitationId: inv.id });
      return;
    }
    const clearsCopyError = [COPY_FAILED_MESSAGE, LINK_CHANGED_MESSAGE, LINK_UNAVAILABLE_MESSAGE].includes(
      state.actionError ?? '',
    );
    update({
      copiedInvitationId: inv.id,
      copyFallbackInvitationId: otherFallback,
      actionError: clearsCopyError ? null : state.actionError,
      actionErrorInvitationId: clearsCopyError ? null : state.actionErrorInvitationId,
    });
    later(COPIED_CUE_MS, () => {
      if (state.copiedInvitationId === inv.id) update({ copiedInvitationId: null });
    });
  };

  const revoke = async (inv: AdminInvitation): Promise<boolean> => {
    if (disposed || inv.id in state.pending) return false;
    if (!deps.confirm(revokeConfirmation(inv.email))) return false;
    const gen = generation;
    setPending(inv.id, 'revoke');
    update({ actionError: null, actionErrorInvitationId: null });
    let revoked = false;
    let failure: string | null = null;
    try {
      await deps.revokeInvitation(inv.id);
      revoked = true;
    } catch (err: unknown) {
      failure = mapInvitationActionFailure(err, 'revoke');
    }
    if (!alive(gen)) return false;
    if (revoked) {
      sendKeys.delete(inv.id);
      cooldownCancels.get(inv.id)?.();
      cooldownCancels.delete(inv.id);
      forgetCopied(inv.id);
      forgetFallback(inv.id);
      clearSendBlock(inv.id);
    } else {
      update({ actionError: failure, actionErrorInvitationId: inv.id });
    }
    // Success and every failure hold the row until the caller's reload lands: after a success
    // the row still shows Pending with its now-dead link; a lost response or the 30 s timeout may
    // follow a revoke the server applied; a 409 or 404 says the row changed. Send, Copy and
    // Revoke stay disabled meanwhile, with Retry if the reload fails.
    await reconcileLink(inv.id, gen);
    return revoked;
  };

  const retryReconcile = async (invitationId: string): Promise<boolean> => {
    if (disposed) return false;
    if (state.pending[invitationId] !== 'reconcile' || !(invitationId in state.reconcileFailed)) return false;
    const gen = generation;
    const decide = afterReconcile.get(invitationId);
    const released = await reconcileLink(invitationId, gen);
    if (!released || !alive(gen)) return false;
    afterReconcile.delete(invitationId);
    decide?.();
    return true;
  };

  const dispose = () => {
    disposed = true;
    generation += 1;
    cooldownCancels.forEach((cancel) => cancel());
    cooldownCancels.clear();
    timers.forEach((timer) => clearTimeout(timer));
    timers.clear();
    afterReconcile.clear();
  };

  return { getState: () => state, send, copyLink, revoke, retryReconcile, dispose };
}
